import React, { useRef, useState } from "react";

import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import type {
  FetchedRecipe,
  MessageResponse,
  OverlayState,
  PaginatedRecipes,
  RecipeListItem,
  RecipeListQuery,
} from "../../features/recipes/recipeTypes";
import { makePostApiCall } from "../../shared/makeApiCall";
import { generateUniqueNumber } from "../../shared/utility";

const emptyOverlay: OverlayState = { childComponent: null, open: false };

interface ApiCallResult<T> {
  success?: boolean;
  data?: T;
  errorStatus?: number;
}

export function RecipesViewModel(
  logout: () => void,
  addSnackbar?: (snackbar: SnackbarData) => void,
) {
  const [emptyModal, setEmptyModelOpen] = useState<OverlayState>(emptyOverlay);
  const [loading, setLoading] = useState(false);
  const [recipes, setRecipes] = useState<PaginatedRecipes>({ data: [] });
  const [fetchedRecipe, setFetchedRecipe] = useState<
    Partial<FetchedRecipe> | Record<string, never>
  >({});
  const lastQuery = useRef<RecipeListQuery>({ search: null, page: 1 });

  const getRecipes = async (search: string | null, page: number) => {
    lastQuery.current = { search, page };
    setLoading(true);
    const result = (await makePostApiCall("recipes", {
      page,
      search,
    })) as ApiCallResult<PaginatedRecipes>;
    setLoading(false);
    if (result.success && result.data) {
      setRecipes(result.data);
    } else if (result.errorStatus === 401) {
      logout();
    }
  };

  const fetchRecipeByPlu = async (plu?: string | null) => {
    if (!plu) return;
    const result = (await makePostApiCall(
      `fetchRecipeByPLU/${plu}`,
      {},
    )) as ApiCallResult<FetchedRecipe>;
    if (result.success && result.data) {
      setFetchedRecipe(result.data);
    } else if (result.errorStatus === 401) {
      logout();
    }
  };

  const deleteRecipe = async (id: number) => {
    setLoading(true);
    const result = (await makePostApiCall(
      `deleteRecipe/${id}`,
      {},
    )) as ApiCallResult<MessageResponse>;
    setLoading(false);
    if (result.success) {
      setEmptyModelOpen(emptyOverlay);
      void getRecipes(lastQuery.current.search, lastQuery.current.page);
      addSnackbar?.(
        new SnackbarData(
          generateUniqueNumber(),
          result.data?.message ?? "Recipe deleted",
          SnackbarType.Success,
        ),
      );
    } else if (result.errorStatus === 401) {
      logout();
    }
  };

  const askDeleteRecipe = (recipe: RecipeListItem) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteRecipe(recipe.id)}
          message={`Are you sure you want to delete  ${recipe.recipe_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return {
    askDeleteRecipe,
    emptyModal,
    fetchRecipeByPlu,
    fetchedRecipe,
    getRecipes,
    loading,
    recipes,
    setEmptyModelOpen,
  };
}
